import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const formatPdfDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
    const dmyMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmyMatch) return value;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${date.getFullYear()}`;
};

export async function downloadDailyFeeReportPDF({
  rows,
  columns,
  fromDate,
  toDate,
  paymentMode,
  collegeDetails,
  logoBase64, // pass base64 or url if available
}) {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  let startY = 10;

  // --- Header: Logo left, text centered ---
  if (collegeDetails) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const logoWidth = 28;
    const logoHeight = 28;
    const logoY = startY;
    // 1. Draw logo on left
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, "PNG", margin, logoY, logoWidth, logoHeight);
      } catch (e) {}
    }
    // 2. Centered text block (college name, etc.)
    let textBlockY = logoY + 2;
    let textX = pageWidth / 2;
    // College Name
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(collegeDetails.college_name?.toUpperCase() || "COLLEGE NAME", textX, textBlockY + 3, {
      align: "center",
    });
    // Autonomous + Accreditation
    const autoAcc = `${collegeDetails.is_autonomous ? "(AUTONOMOUS" : ""}${
      collegeDetails.accreditation
        ? (collegeDetails.is_autonomous ? "; " : "(") + collegeDetails.accreditation
        : ""
    }${collegeDetails.is_autonomous || collegeDetails.accreditation ? ")" : ""}`;
    if (autoAcc && autoAcc !== "") {
      doc.setFontSize(11);
      doc.setFont("helvetica", "italic");
      doc.text(autoAcc, textX, textBlockY + 8, { align: "center" });
    }
    // Affiliation
    if (collegeDetails.affiliation) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "italic");
      doc.text(collegeDetails.affiliation, textX, textBlockY + 12, { align: "center" });
    }
    // Report Title with date range beside in brackets
    let dateRange = `(From: ${formatPdfDate(fromDate) || "-"} To: ${formatPdfDate(toDate) || "-"})`;
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    const title = "Daily Fee Collection Report ";
    const titleWidth = doc.getTextWidth(title);
    const dateRangeWidth = doc.getTextWidth(dateRange);
    const totalWidth = titleWidth + 6 + dateRangeWidth;
    const startX = textX - totalWidth / 2;
    doc.text(title, startX, textBlockY + 20, { align: "left" });
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(dateRange, startX + titleWidth + 6, textBlockY + 20, { align: "left" });
    // Payment Mode (below)
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    let filterInfo = `Payment Mode: ${paymentMode || "All"}`;
    doc.text(filterInfo, textX, textBlockY + 39, { align: "center" });
    startY = logoY + logoHeight + 10;
  } else {
    // Fallback
    doc.setFontSize(16);
    doc.text("Daily Fee Report", 14, 10);
    doc.setFontSize(11);
    let info = `From: ${formatPdfDate(fromDate) || "-"}  To: ${
      formatPdfDate(toDate) || "-"
    }  Payment Mode: ${paymentMode || "All"}`;
    doc.text(info, 14, 18);
    startY = 25;
  }

  // Group rows by receipt_date
  const tableColumns = ["SNo", "Date", "Receipt ID", "Roll No", "Amount", "Remarks"];
  let grandTotal = 0;

  rows.forEach((row) => {
    grandTotal += Number(row.paid_amount || 0);
  });
  const feeHeadGroups = {};

  rows.forEach((row) => {
    const feeHead = row.fee_head_name || "Others";

    if (!feeHeadGroups[feeHead]) {
      feeHeadGroups[feeHead] = [];
    }

    feeHeadGroups[feeHead].push(row);
  });
  // Prepare tableRows with subtotals
  let tableRows = [];
  let serialNo = 1;

  Object.keys(feeHeadGroups)
    .sort()
    .forEach((feeHead) => {
      let feeHeadTotal = 0;

      // Fee Head Title Row
      tableRows.push([
        {
          content: feeHead.toUpperCase(),
          colSpan: 6,
          styles: {
            fontStyle: "bold",
            fillColor: [220, 220, 220],
            textColor: [0, 0, 0],
            halign: "left",
          },
        },
      ]);

      feeHeadGroups[feeHead].forEach((row) => {
        const amount = Number(row.paid_amount || 0);

        feeHeadTotal += amount;

        tableRows.push([
          serialNo++,
          formatPdfDate(row.receipt_date),
          row.fee_receipt_id || "",
          row.student_rollno || "",
          amount.toFixed(2),
          formatRemarksForColumn(row.remarks || "", 100),
        ]);
      });

      // Fee Head Total
      tableRows.push([
        "",
        {
          content: `${feeHead} Total`,
          colSpan: 3,
          styles: {
            halign: "right",
            fontStyle: "bold",
          },
        },
        {
          content: feeHeadTotal.toFixed(2),
          styles: {
            halign: "right",
            fontStyle: "bold",
          },
        },
        "",
      ]);
    });
  tableRows.push([
    "",
    {
      content: "GRAND TOTAL",
      colSpan: 3,
      styles: {
        halign: "right",
        fontStyle: "bold",
        fillColor: [200, 200, 200],
      },
    },
    {
      content: grandTotal.toFixed(2),
      styles: {
        halign: "right",
        fontStyle: "bold",
        fillColor: [200, 200, 200],
      },
    },
    "",
  ]);
  const columnStyles = {
    0: { cellWidth: 18, halign: "center" }, // SNo
    1: { cellWidth: 22, halign: "center" }, // Date
    2: { cellWidth: 25, halign: "center" }, // Receipt ID
    3: { cellWidth: 30, halign: "center" }, // Roll No
    4: { cellWidth: 22, halign: "right" }, // Amount
    5: { cellWidth: 70, halign: "left" }, // Remarks
  };

  autoTable(doc, {
    head: [tableColumns],
    body: tableRows,
    startY: startY,
    styles: {
      fontSize: 10,
      cellPadding: 1,
      lineColor: [0, 0, 0],
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 10,
      halign: "center",
      valign: "middle",
      cellPadding: 3,
      lineWidth: 0.2,
    },
    columnStyles: columnStyles,
    theme: "grid",
    tableWidth: "auto",
    margin: { left: 14, right: 14 },
    didParseCell: function (data) {
      data.cell.styles.textColor = [0, 0, 0];
      if (data.column.index === 3) {
        data.cell.styles.fontSize = 10;
        data.cell.styles.overflow = "linebreak";
      }
      if (data.column.index === 5) {
        data.cell.styles.fontSize = 10;
        data.cell.styles.overflow = "linebreak";
        data.cell.styles.minCellHeight = 8;
        data.cell.styles.cellPadding = { top: 1, bottom: 1, left: 2, right: 2 };
      }
      if (data.column.index === 0 || data.column.index === 1 || data.column.index === 2) {
        data.cell.styles.halign = "center";
      }
    },
  });

  doc.save("DailyFeeReport.pdf");
}

function formatRemarksForColumn(remarks, maxWidth) {
  if (!remarks) return "";
  const remarksStr = String(remarks);
  let formattedRemarks = remarksStr.replace(/,/g, ", ");
  const ids = formattedRemarks.split(",").map((id) => id.trim());
  const charsPerLine = Math.floor(maxWidth * 1.5);
  let result = [];
  let currentLine = "";

  ids.forEach((id, index) => {
    if (!id) return;
    if ((currentLine + (currentLine ? ", " : "") + id).length <= charsPerLine) {
      if (currentLine) {
        currentLine += ", " + id;
      } else {
        currentLine = id;
      }
    } else {
      if (currentLine) result.push(currentLine);
      currentLine = id;
    }
    if (index === ids.length - 1) result.push(currentLine);
  });

  return result.join("\n");
}
